import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { classNames } from "primereact/utils";

import { DisabledTooltip, Icon } from "@/ds";
import {
  ACOES_LINHA_LABEL,
  COLUNA_CLASSIFICACAO,
  COLUNA_NC,
  COLUNA_NOME,
  COLUNA_TOTAL,
  EXPANDIR_LABEL,
  LUPA_LABEL,
  RECOLHER_LABEL,
  TABELA_LABEL,
  TOOLTIP_ICONE,
} from "../planejamento-controle.constants";
import type { LinhaPlanejamento } from "../types-planejamento-controle";
import styles from "./planejamento-tabela.module.scss";
import type { PlanejamentoTabelaProps } from "./types-planejamento-tabela";

const POR_NIVEL: Record<LinhaPlanejamento["nivel"], string | undefined> = {
  "centro-custo": styles.centro,
  tipo: styles.tipo,
  escopo: styles.escopo,
  categoria: styles.categoria,
  facilidades: styles.facilidades,
  total: styles.totalGeral,
};

// Categoria, Facilidades e Total não têm o espaço do chevron: o texto começa no recuo do nível (Figma `c-nome`).
const SEM_CHEVRON: ReadonlySet<LinhaPlanejamento["nivel"]> = new Set([
  "categoria",
  "facilidades",
  "total",
]);

/**
 * Tabela hierárquica Centro de Custo → Tipo → Escopo (Figma `plan-controle/tabela-arvore`): linhas já prontas no ViewModel,
 * chevron para expandir e recolher, lupa do escopo e ⋮ do escopo e do Tipo. Passiva: o menu ⋮ é um só, na View do componente, aberto pela linha clicada.
 */
export function PlanejamentoTabelaView({
  linhas,
  competencias,
  colunasTabela,
  compacta,
  alternar,
  abrirDetalhe,
  abrirMenu,
}: PlanejamentoTabelaProps) {
  return (
    <div
      className={classNames(styles.tabela, compacta && styles.compacta)}
      role="table"
      aria-label={TABELA_LABEL}
      data-testid="planejamento-tabela"
    >
      <div
        className={classNames(styles.linha, styles.cabecalho)}
        style={{ gridTemplateColumns: colunasTabela }}
        role="row"
      >
        <span className={styles.nome} role="columnheader">
          {COLUNA_NOME}
        </span>
        <span className={styles.classificacao} role="columnheader">
          {COLUNA_CLASSIFICACAO}
        </span>
        <span className={classNames(styles.valor, styles.total)} role="columnheader">
          {COLUNA_TOTAL}
        </span>
        <span className={classNames(styles.valor, styles.nc)} role="columnheader">
          {COLUNA_NC}
        </span>
        {competencias.map((competencia) => (
          <span
            key={competencia}
            className={classNames(styles.valor, styles.mes)}
            role="columnheader"
          >
            {competencia}
          </span>
        ))}
        <span className={styles.sobra} aria-hidden />
        <span className={styles.acoes} role="columnheader" aria-hidden />
      </div>
      {linhas.map((linha) => (
        <div
          key={linha.key}
          className={classNames(
            styles.linha,
            POR_NIVEL[linha.nivel],
            linha.expandida && styles.aberta,
            linha.saldoADestinar && styles.saldoADestinar,
          )}
          style={{ gridTemplateColumns: colunasTabela }}
          role="row"
          data-testid={linha.testIds.linha}
        >
          <span className={styles.nome} role="cell">
            {linha.expansivel ? (
              <Button
                type="button"
                text
                rounded
                severity="secondary"
                className={classNames("p-button-row-actions", styles.chevron)}
                icon={<Icon name={linha.expandida ? "chevron-down" : "chevron-right"} size={14} />}
                aria-label={`${linha.expandida ? RECOLHER_LABEL : EXPANDIR_LABEL} ${linha.nome}`}
                tooltip={linha.expandida ? RECOLHER_LABEL : EXPANDIR_LABEL}
                tooltipOptions={TOOLTIP_ICONE}
                aria-expanded={linha.expandida}
                onClick={() => alternar(linha.key)}
              />
            ) : SEM_CHEVRON.has(linha.nivel) ? null : (
              <span className={styles.chevron} aria-hidden />
            )}
            <span className={styles.texto}>
              {linha.nome}
              {linha.estado ? (
                <DisabledTooltip content={linha.estadoTitulo}>
                  <Tag
                    value={linha.estado}
                    severity={linha.saldoADestinar ? "danger" : "warning"}
                    rounded
                    className={styles.estado}
                    icon={linha.saldoADestinar ? undefined : "pi pi-hourglass"}
                    tabIndex={linha.estadoTitulo ? 0 : undefined}
                    data-testid={linha.testIds.estado}
                  />
                </DisabledTooltip>
              ) : null}
            </span>
          </span>
          <span className={styles.classificacao} role="cell">
            {linha.classificacao ? (
              <Tag
                value={linha.classificacao}
                severity="info"
                data-testid={linha.testIds.classificacao}
              />
            ) : null}
          </span>
          <span
            className={classNames(styles.valor, styles.total)}
            role="cell"
            data-testid={linha.testIds.total}
          >
            {linha.total}
          </span>
          <span className={classNames(styles.valor, styles.nc)} role="cell">
            {linha.nc}
          </span>
          {linha.meses.map((mes, indice) => (
            <span
              key={competencias[indice]}
              className={classNames(styles.valor, styles.mes)}
              role="cell"
            >
              {mes}
            </span>
          ))}
          <span className={styles.sobra} aria-hidden />
          <span className={styles.acoes} role="cell">
            {linha.comLupa ? (
              <Button
                type="button"
                text
                rounded
                severity="secondary"
                className="p-button-row-actions"
                icon={<Icon name="search" size={14} />}
                aria-label={`${LUPA_LABEL}: ${linha.nome}`}
                tooltip={LUPA_LABEL}
                tooltipOptions={TOOLTIP_ICONE}
                onClick={() => abrirDetalhe(linha.entidadeId)}
                data-testid={linha.testIds.lupa}
              />
            ) : null}
            {linha.menu ? (
              <Button
                type="button"
                text
                rounded
                severity="secondary"
                className="p-button-row-actions"
                icon={<Icon name="ellipsis-v" size={14} />}
                aria-label={`${ACOES_LINHA_LABEL}: ${linha.nome}`}
                tooltip={ACOES_LINHA_LABEL}
                tooltipOptions={TOOLTIP_ICONE}
                aria-haspopup="menu"
                onClick={(evento) => abrirMenu(evento, linha)}
                data-testid={linha.testIds.menu}
              />
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}
